import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";

// Single source of truth for filter state on /Buscar.
//
// Spanish URL keys ↔ English state keys. The URL is the canonical store;
// React state is initialized from SSR `initialParams` and kept in sync via
// router.replace({shallow: true}). Default-state keys are stripped from the
// URL so it stays clean — `isDefault(key, value)` is the gate.
//
// State shape matches what the backend hooks (`useGetPropertiesQuery`,
// `useGetDevelopmentsQuery`) expect — arrays where the API takes arrays,
// scalars where it takes scalars. The popovers and the layout read/write
// through `setFilter` / `clearFilter` so all URL writes funnel through one
// place.

const DEFAULT_COMPANY = [
    "GUIA",
    "GUIACDMX",
    "GUIAQRO",
    "GUIACABOS",
    "GUIAVALLARTA",
    "GUIATAPALPA",
    "GUIACANCUN",
    "GUIALEON",
    "GUIACHAPALA",
];

// D18.a applied to Buscar: Comprar is the default operation, matching the
// homepage searchbar's default. Default state is the length-1 array
// `[DEFAULT_OPERATION]` so:
//   - `currentOperationValue` reports the explicit default (Comprar highlighted),
//   - `isDefault` strips it from URL,
//   - and the trigger / Limpiar "active" check (anything other than default)
//     correctly stays inactive on cold load.
// State === [] is reserved for explicit user-picked "Todas" — round-trips via
// the `operacion=todas` URL sentinel.
export const DEFAULT_OPERATION = "SALE";

const EMPTY_STATE = Object.freeze({
    type: [],
    operation: [DEFAULT_OPERATION],
    price: [],
    groundSize: [],
    bedrooms: [],
    bathrooms: [],
    halfBathrooms: [],
    parkingLots: [],
    // D17: `q` is the location text (place name from Places autocomplete OR
    // raw fallback string). Stored as a string; the API body translator
    // converts to the backend's array-shape `keywords: [q]` so the wire
    // contract stays unchanged. The field was historically called `keywords`
    // — renamed for clarity now that Buscar only has location-only search.
    q: "",
    latitude: null,
    longitude: null,
    zoom: null,
    order: null,
    includeMode: "both",
    // City + neighborhood are populated when Google Places returns the
    // matching address components. Backend treats them as exact-match
    // text filters (`d.location_props->>'neighborhood' = ...`), which
    // is more accurate than the lat/lng + radius circle when the user
    // picked a named area.
    city: "",
    neighborhood: "",
    company: DEFAULT_COMPANY,
    radius: 5000,
    // Search boundary stack (P8.5 multi-polygon). Empty array = no boundary.
    // Each entry: `{ shape: 'circle' | 'polygon', coords, origin: 'place' | 'draw' }`
    //   - circle coords: `{ center: { lat, lng }, radius: meters }`
    //   - polygon coords: `Array<{ lat, lng }>`
    //   - origin: 'place' = auto from Places pick (or future INEGI), gets
    //     D21 grace; 'draw' = manually drawn, exact match.
    // Behavior:
    //   - Place pick REPLACES the entire stack with a single circle.
    //   - Manual draw APPENDS to the stack ("Dibujar perímetro adicional").
    //   - Borrar perímetros clears all in one shot.
    // URL round-trip (D20): only origin='draw' entries serialize (repeated
    // `polygon=lat,lng;...` keys via params.append); origin='place' circles
    // continue to round-trip via the existing q+lat+lng+radio path. D17
    // classifies polygons as map state — Limpiar leaves them alone.
    polygons: [],
});

// Order-insensitive array equality — dedicated pages preset `type` as a
// value-set, and the popovers may commit the same set in a different order.
export const sameValueSet = (a, b) => {
    const left = Array.isArray(a) ? a : [];
    const right = Array.isArray(b) ? b : [];
    return left.length === right.length && left.every((v) => right.includes(v));
};

// What value counts as "default" per filter — i.e. URL should not write this
// key. `defaults` is the page's default state (EMPTY_STATE on /Buscar; a
// preset overlay on dedicated pages), so a page's own preset stays out of
// the URL while any user deviation writes.
const isDefault = (key, value, defaults = EMPTY_STATE) => {
    switch (key) {
        case "type":
        case "operation":
            // Deviation from the page default writes via serializeForUrl —
            // including operation's explicit empty-array Todas pick.
            return sameValueSet(value, defaults[key]);
        case "price":
        case "groundSize":
        case "bedrooms":
        case "bathrooms":
        case "halfBathrooms":
        case "parkingLots":
            return !value || value.length === 0;
        case "q":
            return !value;
        case "latitude":
        case "longitude":
        case "zoom":
        case "order":
            return value === null || value === undefined || value === "";
        case "city":
        case "neighborhood":
            return !value || value === "";
        case "includeMode":
            return value === "both" || !value;
        case "company":
            return true; // never written to URL — internal defaults
        case "radius":
            // Round-trips via URL `radio=` so shareable links restore the
            // search circle (the boundary polygon re-derives from URL state
            // on cold load). The default radius is stripped.
            return value === null || value === undefined || value === defaults.radius;
        case "polygons":
            // Only origin='draw' entries ever serialize, so a page's default
            // boundary (origin 'floor') is invisible here either way; the
            // length check just short-circuits the common empty case.
            return !Array.isArray(value) || value.length === 0;
        default:
            return value === null || value === undefined || value === "";
    }
};

// Serialize a state value to its URL string form, per key. Mirrors the parsing
// in `src/pages/Buscar/index.js` (getServerSideProps).
const serializeForUrl = (key, value) => {
    switch (key) {
        case "type":
            return value.join(",");
        case "operation":
            // Default (Comprar = [DEFAULT_OPERATION]) is already stripped by
            // isDefault before reaching here. What we serialize:
            //   - length 0 ([])         → "todas" sentinel (explicit user pick)
            //   - length 3 (legacy)     → "todas" sentinel (same render)
            //   - length 1 single op    → that value (e.g. "LEASE", "PRESALE")
            //   - length 2 (no UI path) → null (defensive — should not occur)
            if (value.length === 0 || value.length === 3) return "todas";
            return value.length === 1 ? value[0] : null;
        case "price":
        case "groundSize":
            if (value.length === 2) return `${value[0]}-${value[1]}`;
            if (value.length === 1) return `${value[0]}-`;
            return null;
        case "bedrooms":
        case "bathrooms":
        case "halfBathrooms":
        case "parkingLots":
            if (value.length === 0) return null;
            // Length 1 (compact): "2" → "2+" semantic (>=).
            // Length 2 (range): "2-2" → exact (BETWEEN), or "2-4" → between min and max.
            if (value.length === 1) return String(value[0]);
            return `${value[0]}-${value[1]}`;
        case "q":
            return value || null;
        case "latitude":
        case "longitude":
        case "zoom":
            return value !== null && value !== undefined ? String(value) : null;
        case "order":
        case "includeMode":
            return value || null;
        case "polygons": {
            // D20 contract: one `polygon=lat,lng;lat,lng;...` URL key per
            // polygon (repeated via params.append in buildUrlSearchParams).
            // Only origin='draw' entries serialize — origin='place' (auto from
            // a Places pick) round-trips via q+lat+lng+radio. Circles inside
            // a multi-polygon stack don't serialize either; if a user picks
            // a place AND draws additional polygons, only the drawn ones land
            // in the URL (the place still hydrates from q+lat+lng+radio via
            // derivePolygons on cold load).
            if (!Array.isArray(value) || value.length === 0) return null;
            const encoded = value
                .filter(
                    (p) =>
                        p &&
                        p.shape === "polygon" &&
                        p.origin === "draw" &&
                        Array.isArray(p.coords) &&
                        p.coords.length >= 3
                )
                .map((p) => p.coords.map(({ lat, lng }) => `${lat},${lng}`).join(";"));
            return encoded.length === 0 ? null : encoded;
        }
        default:
            return value !== null && value !== undefined ? String(value) : null;
    }
};

// Map state key → URL key (Spanish). Inverse of the SSR parser's mapping.
const STATE_TO_URL_KEY = {
    type: "tipo",
    operation: "operacion",
    price: "precio",
    groundSize: "superficie",
    bedrooms: "recamaras",
    bathrooms: "banos",
    halfBathrooms: "mediosBanos",
    parkingLots: "estacionamientos",
    q: "q",
    latitude: "lat",
    longitude: "lng",
    zoom: "z",
    order: "orden",
    includeMode: "incluir",
    city: "ciudad",
    neighborhood: "colonia",
    radius: "radio",
    polygons: "polygon",
};

// State keys that NEVER appear in the URL (internal-only).
const URL_EXCLUDED_KEYS = new Set(["company"]);

// Exported for the pages that need to build a /Buscar URL from a state
// snapshot (e.g. redirecting an out-of-boundary place pick with the user's
// non-default filters carried along).
const buildUrlSearchParams = (state, defaults = EMPTY_STATE) => {
    const params = new URLSearchParams();
    for (const stateKey of Object.keys(state)) {
        if (URL_EXCLUDED_KEYS.has(stateKey)) continue;
        const value = state[stateKey];
        if (isDefault(stateKey, value, defaults)) continue;
        const urlKey = STATE_TO_URL_KEY[stateKey];
        if (!urlKey) continue;
        const serialized = serializeForUrl(stateKey, value);
        if (serialized === null) continue;
        // Array-valued serialization → one URL key per entry (D20 multi-
        // polygon contract). Scalar → standard params.set.
        if (Array.isArray(serialized)) {
            for (const s of serialized) params.append(urlKey, s);
        } else {
            params.set(urlKey, serialized);
        }
    }
    // When a single circle polygon is active, force `radio=` to the polygon's
    // locked coords.radius rather than state.radius. state.radius doubles as
    // the camera-viewport radius (updated by handleMapIdle on every drag) —
    // without this override, dragging after a place pick would mutate URL
    // radio=, and reloading would hydrate the polygon at the user's last
    // viewport radius instead of the original place radius. The polygon's
    // coords.radius is set once at pick/draw time and never mutates, so it's
    // the right source of truth for cold-load polygon hydration. Drawn and
    // multi-polygon states round-trip via repeated `polygon=` URL keys and
    // don't depend on radio= for hydration, so the override is gated to the
    // single-circle case.
    if (Array.isArray(state.polygons) && state.polygons.length === 1) {
        const only = state.polygons[0];
        if (only?.shape === "circle" && Number.isFinite(only?.coords?.radius)) {
            params.set("radio", String(only.coords.radius));
        }
    }
    return params;
};

// D21 grace radius: place-derived boundaries (Places autocomplete pick, future
// INEGI sublocality polygons) get a generosity buffer added at the apiBody
// boundary — captures listings just outside Google's tight place definitions
// (Providencia is divided into discrete subsections, "Guadalajara" excludes
// metro municipios, etc.). Manual draw stays exact — intent is meditated.
//
// Formula: percentage of the place's radius, clamped to a [floor, ceiling]
// window. Geography-agnostic (radius is already viewport-derived per D15, so
// scales with whatever the place is — colonia, city, state). For polygons
// (future), use bbox max-dimension as the size signal.
const GRACE_RATIO = 0.15;
const GRACE_MIN_M = 800;
const GRACE_MAX_M = 5000;

const computeGraceM = (polygon) => {
    if (!polygon || polygon.origin !== "place") return 0;
    let sizeM = 0;
    if (polygon.shape === "circle") {
        sizeM = polygon.coords.radius;
    } else if (polygon.shape === "polygon" && Array.isArray(polygon.coords)) {
        // bbox half-max-dimension in meters, treated as a radius-equivalent.
        let minLat = Infinity;
        let maxLat = -Infinity;
        let minLng = Infinity;
        let maxLng = -Infinity;
        for (const v of polygon.coords) {
            if (!Number.isFinite(v?.lat) || !Number.isFinite(v?.lng)) continue;
            if (v.lat < minLat) minLat = v.lat;
            if (v.lat > maxLat) maxLat = v.lat;
            if (v.lng < minLng) minLng = v.lng;
            if (v.lng > maxLng) maxLng = v.lng;
        }
        if (!Number.isFinite(minLat)) return 0;
        const cosLat = Math.cos((((minLat + maxLat) / 2) * Math.PI) / 180);
        const heightM = (maxLat - minLat) * 111_000;
        const widthM = (maxLng - minLng) * 111_000 * cosLat;
        sizeM = Math.max(heightM, widthM) / 2;
    }
    if (sizeM <= 0) return 0;
    return Math.max(GRACE_MIN_M, Math.min(sizeM * GRACE_RATIO, GRACE_MAX_M));
};

// Zillow-style drag-to-rescope. When true, dragging the camera in map view
// drops the polygon as filter (the polygon outline becomes a visual anchor)
// and the current viewport rectangle takes over as the spatial filter — list
// narrows to map bounds, results refetch on each idle. When false (default),
// drag is pure exploration: the polygon stays as the only spatial filter
// regardless of camera position, and no polygon = no spatial filter (backend
// returns everything matching non-spatial filters).
//
// Currently false. The Zillow pattern fits a vast inventory where scope-by-
// pan is the user's main narrowing tool; at our inventory size that pattern
// adds friction (count keeps shifting as you pan) and the refetch-per-drag
// is perceptibly slow. Users who want to scope can use the location search
// or the polygon drawer. Flip back to `true` to restore the Zillow UX —
// no other code changes needed; the flag gates both apiBody's viewport-
// fallback and BuscarLayout's applyBoundsClip prop.
export const VIEWPORT_FILTERS_ON_DRAG = false;

// Convert a circle (center + radius) to a regular-polygon approximation.
// Used by apiBody when the polygon stack mixes shapes (≥1 circle alongside
// drawn polygons) — the backend only accepts a single spatial filter shape,
// so circles get polygonized to fit into the `polygons: Array<...>` contract.
// 32 sides is plenty visual-accurate at any zoom level we render.
const POLYGONIZE_SIDES = 32;
const circleToPolygon = ({ center, radius }) => {
    const verts = [];
    const cosLat = Math.cos((center.lat * Math.PI) / 180) || 1;
    const dLat = radius / 111_320; // ~111.32 km per degree latitude
    const dLng = radius / (111_320 * cosLat);
    for (let i = 0; i < POLYGONIZE_SIDES; i++) {
        const theta = (2 * Math.PI * i) / POLYGONIZE_SIDES;
        verts.push({
            lat: center.lat + dLat * Math.cos(theta),
            lng: center.lng + dLng * Math.sin(theta),
        });
    }
    return verts;
};

// `cameraDragged` is the D17-amend gate: when the user has panned/zoomed off
// the auto-fit polygon position, the polygon becomes decoration and the
// viewport (state.latitude/longitude/radius) takes over as the spatial filter.
// Re-centrar resets cameraDragged back to false and restores the polygon
// filter. BuscarLayout owns the cameraDragged state and passes it in.
const useSearchState = (
    initialParams,
    // `floorFilters`: whether the page's default boundary (origin 'floor')
    // acts as a spatial filter (true — boundary mode) or is purely a visual
    // anchor while the page's other defaults (company) do the scoping
    // (false — anchor mode). User-created boundaries always filter in both
    // modes. Irrelevant on /Buscar, which has no floor entries.
    { cameraDragged = false, mapHidden = false, defaults = EMPTY_STATE, floorFilters = true } = {}
) => {
    const router = useRouter();

    // Hydrate from SSR. Keep the page's defaults for any missing keys.
    const [state, setState] = useState(() => ({
        ...defaults,
        ...initialParams,
    }));

    // Push current state to URL via shallow router.replace. Skips default-state keys.
    const syncUrl = useCallback(
        (nextState) => {
            const params = buildUrlSearchParams(nextState, defaults);
            const search = params.toString();
            router.replace({ pathname: router.pathname, search }, undefined, { shallow: true });
        },
        [router, defaults]
    );

    const setFilter = useCallback(
        (key, value) => {
            setState((prev) => {
                const next = { ...prev, [key]: value };
                syncUrl(next);
                return next;
            });
        },
        [syncUrl]
    );

    // Clearing reverts to the PAGE default, not the empty state — on a city
    // page, clearing the boundary restores the city's floor polygon; on a
    // type page, clearing the type restores the preset. On /Buscar the two
    // are the same object.
    const clearFilter = useCallback(
        (key) => {
            setState((prev) => {
                const next = { ...prev, [key]: defaults[key] };
                syncUrl(next);
                return next;
            });
        },
        [syncUrl, defaults]
    );

    const setFilters = useCallback(
        (patch) => {
            setState((prev) => {
                const next = { ...prev, ...patch };
                syncUrl(next);
                return next;
            });
        },
        [syncUrl]
    );

    // API body in the existing English-keyed shape — what `useGetPropertiesQuery`
    // and `useGetDevelopmentsQuery` already accept. `includeMode` is internal-only
    // (used by the layout to skip one of the hooks); not sent to API.
    //
    // Length-1 semantic: backend `addRangeFilter` (entities `app.service.ts`)
    // now interprets `[n]` as `>= n` natively — the P8.0c fix shipped on prod
    // (boomerang-entities PR #50, commit 0692f2a). The previous frontend
    // workaround (expand length-1 to `[n, BIG]`) is gone; state arrays pass
    // through unchanged.
    const apiBody = useMemo(() => {
        // Spatial filter precedence (P8.5 multi-polygon):
        //   • Empty polygons stack OR cameraDragged → viewport
        //     (state.latitude/longitude/radius — D17 amend).
        //   • Single polygon entry: optimized path per shape.
        //       - shape='circle': lat/lng/radius (+ grace if origin='place').
        //       - shape='polygon': `polygons: [coords]` (+ `graceM` if
        //         origin='place', for future INEGI ST_Buffer support).
        //   • Multiple entries OR a mix: polygonize any circles into
        //     POLYGONIZE_SIDES-gons and send `polygons: [...]` for all. No
        //     grace in the multi case — once the user is composing precise
        //     shapes, silently widening one and not others would be
        //     surprising. The user can always Borrar perímetros and start
        //     fresh with a single place pick to get grace back.
        const { includeMode: _includeMode, q, polygons, zoom: _zoom, ...rest } = state;
        // The stack that actually filters. In anchor mode the page's default
        // boundary (origin 'floor') is scenery — the page's other defaults
        // (company) do the scoping — so only user-created entries reach the
        // backend. In boundary mode (and always on /Buscar) every entry
        // filters.
        const spatialStack = floorFilters
            ? polygons
            : (polygons ?? []).filter((p) => p?.origin !== "floor");
        // Backend's `search` / `searchDevs` expect the legacy `keywords: [string]`
        // shape for the location text. Translate at the boundary so client state
        // can use the cleaner `q: string` field.
        if (q) rest.keywords = [q];
        // `zoom` is a camera-state field (URL round-trip for cold-load
        // camera memory + handleMapIdle pushes on drag). Backend doesn't
        // use it — including it in the body would make every zoom step
        // mutate the RTK Query cache key and trigger a needless refetch
        // (skeleton flash, no data change). Strip at the apiBody boundary.
        // polygonsAsFilter: at least one polygon active. The cameraDragged
        // gate (D17 amend — drag drops polygon as filter, viewport takes
        // over) only applies when VIEWPORT_FILTERS_ON_DRAG is on AND we're
        // in map view, where the user can actually see the camera moving
        // off the polygon. With the Zillow drag-to-rescope behavior off
        // (the default), polygon stays the filter regardless of drag —
        // map is a viewer, not a query.
        const polygonsAsFilter =
            Array.isArray(spatialStack) &&
            spatialStack.length > 0 &&
            (!VIEWPORT_FILTERS_ON_DRAG || mapHidden || !cameraDragged);
        if (polygonsAsFilter) {
            // Whenever the user composes a custom search area (any drawn
            // polygon in the stack, or multiple polygons), drop the place-
            // derived text filters (keywords/city/neighborhood). They were
            // anchored to a single Places pick; keeping them strict-matches
            // results to that place's listings and excludes anything the
            // user added via draw. Single place-only path keeps text filters
            // (D15 strict colonia/city match) so "Providencia" alone stays
            // tight.
            const hasDraw = spatialStack.some((p) => p && p.origin === "draw");
            if (hasDraw) {
                delete rest.keywords;
                delete rest.city;
                delete rest.neighborhood;
            }
            if (spatialStack.length === 1) {
                const p = spatialStack[0];
                const graceM = computeGraceM(p);
                if (p.shape === "polygon" && Array.isArray(p.coords) && p.coords.length >= 3) {
                    rest.polygons = [p.coords];
                    if (graceM > 0) rest.graceM = graceM;
                    delete rest.latitude;
                    delete rest.longitude;
                    delete rest.radius;
                } else if (
                    p.shape === "circle" &&
                    p.coords?.center &&
                    Number.isFinite(p.coords.radius)
                ) {
                    rest.latitude = p.coords.center.lat;
                    rest.longitude = p.coords.center.lng;
                    rest.radius = p.coords.radius + graceM;
                }
            } else {
                // Multi: polygonize circles, send all as polygons[] with no
                // grace. ST_Within union semantic = OR across polygons (P8.1').
                const allCoords = spatialStack
                    .map((p) => {
                        if (
                            p.shape === "polygon" &&
                            Array.isArray(p.coords) &&
                            p.coords.length >= 3
                        ) {
                            return p.coords;
                        }
                        if (
                            p.shape === "circle" &&
                            p.coords?.center &&
                            Number.isFinite(p.coords.radius)
                        ) {
                            return circleToPolygon(p.coords);
                        }
                        return null;
                    })
                    .filter(Boolean);
                if (allCoords.length > 0) {
                    rest.polygons = allCoords;
                    delete rest.latitude;
                    delete rest.longitude;
                    delete rest.radius;
                }
            }
        } else if (!VIEWPORT_FILTERS_ON_DRAG || mapHidden || !cameraDragged) {
            // No polygon active AND any of: (a) Zillow drag-to-rescope is
            // off (always, by default), (b) map is hidden (list view), (c)
            // user hasn't actively dragged the camera. Drop the viewport
            // spatial filter so the backend returns everything matching
            // non-spatial filters. This matches D22's cold-load behavior
            // (no URL camera params → no spatial filter) and means "Borrar
            // perímetro" semantically removes ALL spatial intent: without
            // this gate, deleting the boundary would silently keep
            // filtering by the polygon's center+radius (minus grace), and
            // results would *shrink* on delete (e.g. 187 → 170 for a
            // Guadalajara pick) — confusing to a user expecting "removed
            // constraint = more results."
            //
            // When VIEWPORT_FILTERS_ON_DRAG is true, the (b)/(c) terms
            // restore the Zillow behavior: drag on desktop → viewport
            // becomes the explicit filter.
            // leaves stale lat/lng/radius in state (clearBoundary preserves
            // them per D17 map-state separation), and the backend keeps
            // returning the place's circle even after the polygon is gone.
            // Mobile users toggling list ↔ map view also expect the list to
            // mean "all backend-matching results," not "what's in the
            // invisible map's viewport." Map view restores the viewport
            // filter when mapHidden flips false.
            delete rest.latitude;
            delete rest.longitude;
            delete rest.radius;
        }
        return rest;
    }, [state, cameraDragged, mapHidden, floorFilters]);

    // The user's own boundary entries — a page's default boundary (origin
    // 'floor') is fallback scenery, not a user choice, so every "does the
    // user have a boundary?" gate reads this instead of state.polygons.
    const userPolygons = useMemo(
        () =>
            Array.isArray(state.polygons)
                ? state.polygons.filter((p) => p?.origin !== "floor")
                : [],
        [state.polygons]
    );

    const isDefaultForPage = useCallback(
        (key, value) => isDefault(key, value, defaults),
        [defaults]
    );

    return {
        state,
        setFilter,
        clearFilter,
        setFilters,
        isDefault: isDefaultForPage,
        userPolygons,
        apiBody,
    };
};

export default useSearchState;
export { buildUrlSearchParams, computeGraceM, DEFAULT_COMPANY, EMPTY_STATE, STATE_TO_URL_KEY };

// This file lives under src/pages/ for historical reasons but is a shared
// module, not a route. Next.js still registers it as a page; serve 404 for
// its accidental URL instead of prerendering it (which crashes the build now
// that the app really renders during SSR).
export function getStaticProps() {
    return { notFound: true };
}
